import type { FC } from 'react';
import { Button } from 'primereact/button';
import { PencilRuler } from 'lucide-react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { SkillStatusBadge } from '../common';
import { useBrainBreakNavigation } from '../../custom-hooks/useBrainBreakNavigation';
import { EXIT_SLIP_DEMONSTRATED_LABEL } from '../../businessLogicService/exitSlipSkillPage';
import { SKILL_SUBSTATE_STYLES } from '../../custom-hooks/useCurrentSkillResult';

interface Props {
  // How many of this skill's exit tickets were passed, out of how many infer it — "2/2 Exit tickets"
  // in the design. Both come from the same report record the header's chip reads, which is a second
  // request and can still be in flight: the count line is left out until it lands rather than
  // rendered as "0/0", since the skill being demonstrated is already known without it.
  passedCount?: number;
  relatedCount?: number;
}

/**
 * What the Skill Challenge step shows for a skill the student has already won on exit tickets.
 *
 * It stands in for the challenge itself, which is the point: there is nothing left to prove here, so
 * offering a Learnosity session would be asking them to re-earn a skill they hold. SkillResource picks
 * between this and AssessmentComponent, and this branch deliberately never asks for a session.
 *
 * A sibling of AssessmentCompleted rather than a mode inside it: that component is built end to end on
 * `attemptInformation.totalScore` — the ice cubes, which buttons appear, the substate it writes back to
 * redux, the weekly-goal message — and none of it has an answer for a skill with no attempt behind it.
 * What the two share is the step's chrome and the way out, both of which are small.
 */
export const SkillChallengeDemonstrated: FC<Props> = ({ passedCount, relatedCount }) => {
  const navigate = useNavigate();
  // The same way out as AssessmentCompleted's Move Forward, brain break included: this is the same
  // slot in the student's journey, reached by a different route.
  const handleMoveForward = useBrainBreakNavigation(() => navigate('/'), 'assessment-completed');

  return (
    <div className={`tw-w-full nc_student-skill-content-container `} style={{ height: '-webkit-fill-available' }}>
      <div className="tw-flex tw-flex-row tw-ml-auto tw-p-6 tw-items-center">
        <PencilRuler />
        <span className="sm:tw-text-2xl tw-text-lg tw-font-sora tw-font-bold tw-ml-4">Skill Challenge</span>
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
        className="tw-flex tw-flex-col tw-items-center tw-gap-y-6 tw-py-[24px] tw-px-[12px]"
      >
        <h3 className="tw-m-0 tw-font-atkinson tw-font-normal sm:tw-text-4xl tw-text-xl">
          Done by <b>Exit tickets</b>
        </h3>
        <p className="tw-m-0 tw-max-w-[420px] tw-text-center tw-font-atkinson tw-text-lg">
          You’ve done this skill by completing all your related Exit tickets
        </p>
        <SkillStatusBadge
          status={EXIT_SLIP_DEMONSTRATED_LABEL}
          showIcon
          iconType="check-circle"
          // The same pill the header carries two lines up the page, from the one definition of it —
          // the two are on screen together, so a copy would be a copy that can drift.
          className={SKILL_SUBSTATE_STYLES.DONE_BY_EXIT_TICKETS}
        />
        {!!relatedCount && (
          <p className="tw-m-0 tw-font-atkinson tw-text-base tw-text-neutral-700">
            {passedCount}/{relatedCount} Exit tickets
          </p>
        )}
        <Button
          data-testid="exit-slip-demonstrated-move-forward"
          label="Move forward"
          className="tw-font-atkinson"
          onClick={handleMoveForward}
        />
      </motion.div>
    </div>
  );
};

export default SkillChallengeDemonstrated;
